import {
   useEffect,
   useRef,
   useState,
   type UIEvent,
   type MouseEventHandler,
} from 'react';
import DocumentSortIcon from '../DocumentSortIcon';
import { formatDateString } from '../../lib/date';
import { ClosedEndedStatement } from 'types/interface';
import {
   formatNumber,
   formatIls,
   getRate,
   toIls,
   RateMode,
} from '../../lib/currency';

interface Props {
   investments: ClosedEndedStatement[];
   showIls?: boolean;
   rateMode?: RateMode;
}

export default function InvestmentsSummaryTable({
   investments,
   showIls = false,
   rateMode = 'statementDate',
}: Props) {
   function onSortClick(arg0: string): void {
      throw new Error('Function not implemented.');
   }
   const sortOrder: 'ASC' | 'DESC' | null = null;
   const sortBy: string | undefined = undefined;

   const renderAmount = (investment: ClosedEndedStatement, value: number) => {
      if (!showIls) {
         return formatNumber(value);
      }
      const ils = toIls(value, getRate(investment, rateMode));
      return (
         <>
            <div>{formatNumber(value)}</div>
            <div className="text-gray-400">
               {ils == null ? '—' : formatIls(ils)}
            </div>
         </>
      );
   };

   const [isScrollable, setIsScrollable] = useState(false);
   const [hasScrolled, setHasScrolled] = useState(false);
   const [isAtEnd, setIsAtEnd] = useState(false);
   const scrollContainerRef = useRef<HTMLDivElement | null>(null);

   const updateScrollState = (element: HTMLDivElement) => {
      const scrollLeft = element.scrollLeft;
      const clientWidth = element.clientWidth;
      const scrollWidth = element.scrollWidth;

      setIsScrollable(scrollWidth > clientWidth);
      setHasScrolled(scrollLeft > 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
   };

   const handleTableScroll = (event: UIEvent<HTMLDivElement>) => {
      updateScrollState(event.currentTarget);
   };

   useEffect(() => {
      const element = scrollContainerRef.current;
      if (!element) return;

      updateScrollState(element);
      const handleResize = () => updateScrollState(element);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const rowClick: MouseEventHandler<HTMLTableRowElement> = e => {
      if (!(
         e.target instanceof HTMLInputElement ||
         e.target instanceof HTMLButtonElement ||
         e.target instanceof HTMLAnchorElement
      )) {
         // Handle row click event here
      }
   };

   return (
      <>
         <div className="relative">
            <div
               className="overflow-x-auto"
               ref={scrollContainerRef}
               onScroll={handleTableScroll}
            >
               <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                     <tr>
                        <th
                           onClick={() => onSortClick('fundName')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           {sortBy === 'fundName' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>
                        <th
                           onClick={() => onSortClick('currency')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           Currency
                           {sortBy === 'currency' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>
                        <th
                           onClick={() => onSortClick('date')}
                           className="hidden sm:table-cell cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           As of Date
                           {sortBy === 'date' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>
                        <th
                           onClick={() => onSortClick('commitment')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           Commitment
                           {sortBy === 'commitment' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>

                        <th
                           onClick={() => onSortClick('totalContributions')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           Total Contributions
                           {sortBy === 'totalContributions' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>

                        <th
                           onClick={() => onSortClick('remainingCommitment')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           Remaining Commitment
                           {sortBy === 'remainingCommitment' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>
                        <th
                           onClick={() => onSortClick('totalDistributions')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           Total Distributions
                           {sortBy === 'totalDistributions' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>

                        <th
                           onClick={() => onSortClick('nav')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           NAV
                           {sortBy === 'nav' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th>

                        {/* <th
                           onClick={() => onSortClick('irr')}
                           className="cursor-pointer px-2 sm:px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                           IRR
                           {sortBy === 'irr' && (
                              <DocumentSortIcon order={sortOrder} />
                           )}
                        </th> */}
                     </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                     {investments.map((investment, index) => (
                        <tr
                           key={index}
                           className={
                              'bg-white hover:bg-gray-50 w-full px-2 py-1 sm:px-6 sm:py-4 border rounded-lg mb-4 md:mb-0'
                           }
                           onClick={rowClick}
                           data-testid="investment-row"
                        >
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-gray-500">
                              <div className="text-xs font-bold sm:text-sm sm:font-normal">
                                 {investment.account.fund.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                 {investment.account.name}
                              </div>
                              <div className="text-gray-400 text-xs mt-2 sm:hidden sm:text-gray-500">
                                 {formatDateString(investment.publishedAt)}
                              </div>
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {investment.account.currency}
                           </td>
                           <td className="hidden sm:table-cell px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {formatDateString(investment.publishedAt)}
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {renderAmount(
                                 investment,
                                 investment.totalCommitment,
                              )}
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {renderAmount(
                                 investment,
                                 investment.capitalCalled,
                              )}
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {renderAmount(
                                 investment,
                                 investment.remainingCommitment,
                              )}
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {renderAmount(investment, investment.distributed)}
                           </td>
                           <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {renderAmount(investment, investment.nav)}
                           </td>
                           {/* <td className="px-2 py-1 sm:px-6 sm:py-4 text-left whitespace-nowrap text-xs text-gray-500">
                              {investment.irr}%
                           </td> */}
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>

            {isScrollable && !isAtEnd && (
               <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white via-white/70 to-transparent" />
            )}
            {hasScrolled && (
               <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white via-white/70 to-transparent" />
            )}
         </div>
      </>
   );
}
